import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/**
 * M25 L25.03 — Model drift monitor.
 *
 * Lists AI models (`GET /v1/ai/models`) and, for each, checks drift status via
 * `GET /v1/ai/evaluations/model/:modelId/drift`.
 */
interface ModelRow {
  readonly modelId: string;
  readonly modelCode: string;
  readonly modelName: string;
  readonly modelType: string;
  readonly status: string;
}

interface DriftState {
  readonly loading: boolean;
  readonly drifted: boolean | null;
}

export function ModelDriftPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [models, setModels] = useState<ModelRow[] | null>(null);
  const [drift, setDrift] = useState<Record<string, DriftState>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: ModelRow[] }>('/v1/ai/models', { token })
      .then(async (r) => {
        if (!alive) return;
        const rows = r.data ?? [];
        setModels(rows);
        setDrift(Object.fromEntries(rows.map((m) => [m.modelId, { loading: true, drifted: null }])));
        await Promise.all(rows.map(async (m) => {
          try {
            const d = await apiRequest<{ data: { drifted: boolean } }>(
              `/v1/ai/evaluations/model/${encodeURIComponent(m.modelId)}/drift`, { token },
            );
            if (alive) setDrift((prev) => ({ ...prev, [m.modelId]: { loading: false, drifted: d.data?.drifted ?? false } }));
          } catch {
            if (alive) setDrift((prev) => ({ ...prev, [m.modelId]: { loading: false, drifted: null } }));
          }
        }));
      })
      .catch((e) => { if (alive) { setError((e as Error).message); setModels([]); } });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="model-drift">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('m25.drift.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('m25.drift.subtitle')}
      </p>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!models ? (
        <p>{t('m25.drift.loading')}</p>
      ) : models.length === 0 ? (
        <p data-testid="models-empty" style={{ color: 'var(--color-text-muted)' }}>{t('m25.drift.empty')}</p>
      ) : (
        <table aria-label="drift-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--color-border-default)', textAlign: 'left' }}>
              <th style={{ padding: '8px 4px' }}>{t('m25.drift.colModel')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m25.drift.colType')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m25.drift.colStatus')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m25.drift.colDrift')}</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => {
              const d = drift[m.modelId];
              const label = !d || d.loading ? '…' : d.drifted === null ? t('m25.drift.na') : d.drifted ? t('m25.drift.drifted') : t('m25.drift.ok');
              const color = d?.drifted ? 'var(--color-danger)' : d?.drifted === false ? 'var(--color-success)' : 'var(--color-text-muted)';
              return (
                <tr key={m.modelId} data-testid={`model-row-${m.modelId}`} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                  <td style={{ padding: '8px 4px' }}>{m.modelName}</td>
                  <td style={{ padding: '8px 4px' }}>{m.modelType}</td>
                  <td style={{ padding: '8px 4px' }}>{m.status}</td>
                  <td style={{ padding: '8px 4px', fontWeight: 600, color }} data-testid={`drift-${m.modelId}`}>{label}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </main>
  );
}
